/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags, but not destructive removals or granting
 *                   privileged roles
 *   - support     — read users + audit, ban/unban + revoke sessions (no role/flag
 *                   changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

import RBAC_CONTRACT from "./rbac-contract.json";

export const ADMIN_ROLES = ["super_admin", "admin", "support", "read_only", "user"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  | "users.remove"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "metrics.read"
  | "moderation.read"
  | "tickets.read"
  /**
   * Mutar contenido del back-office: temas, fuentes, plantillas y campañas de
   * email. Espejo de `Capability::ManageContent`. Faltaba en esta lista, así que
   * la UI no tenía forma de ocultar esas acciones a `read_only` — y el backend
   * tampoco las gateaba: eran ejecutables de verdad.
   */
  | "content.write";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  "integrations.testEmail",
  "moderation.read",
  "tickets.read",
  "content.write",
];

const ADMIN_CAPS: AdminCapability[] = [...SUPPORT_CAPS, "users.setRole", "flags.write"];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  read_only: READ_CAPS,
  user: [],
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

/**
 * Orden jerárquico de los roles, LEÍDO del contrato compartido — no copiado.
 *
 * El modelo tiene que existir en dos runtimes (el servidor decide, la UI oculta
 * lo que el servidor va a rechazar), pero el contrato es uno solo:
 * `rbac-contract.json`. Rust lo lee en sus tests y afirma que su `Role::rank()`
 * coincide; aquí se usa directamente en tiempo de ejecución, así que este lado
 * ni siquiera puede desincronizarse.
 *
 * Se escribe clave a clave (en vez de `Object.fromEntries(...) as Record<…>`)
 * para que el compilador COMPRUEBE que el contrato trae los cinco roles, en
 * lugar de que una aserción se lo crea sin mirar.
 */
const ROLE_RANK: Record<AdminRole, number> = {
  super_admin: RBAC_CONTRACT.roles.super_admin.rank,
  admin: RBAC_CONTRACT.roles.admin.rank,
  support: RBAC_CONTRACT.roles.support.rank,
  read_only: RBAC_CONTRACT.roles.read_only.rank,
  user: RBAC_CONTRACT.roles.user.rank,
};

/**
 * Roles que `actorRole` puede CONCEDER desde el gestor de usuarios.
 *
 * Sustituye a la vieja constante `ASSIGNABLE_ROLES`, que era simplemente
 * `ADMIN_ROLES` — la lista entera, para cualquiera. El nombre no obligaba a
 * decir "asignable ¿por quién?", así que el selector de rol ofrecía
 * `super_admin` a un `admin`, y el backend lo aceptaba: una escalada de
 * privilegios de un solo salto.
 *
 * Regla (idéntica a `can_grant_role` en el backend): hace falta la capacidad
 * `users.setRole` y nunca se concede por encima del propio rango.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  if (!roleCan(actorRole, "users.setRole")) {
    return [];
  }
  const actorRank = ROLE_RANK[toAdminRole(actorRole)];
  return ADMIN_ROLES.filter((role) => ROLE_RANK[role] <= actorRank);
}

/**
 * ¿Puede `actorRole` administrar a un usuario cuyo rol ACTUAL es `targetRole`?
 * Espejo de `can_administer`: estrictamente por debajo, salvo `super_admin`,
 * que puede con sus pares (si no, uno deshonesto sería intocable).
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  if (actor === "super_admin") {
    return true;
  }
  return ROLE_RANK[toAdminRole(targetRole)] < ROLE_RANK[actor];
}

/**
 * Mapa capacidad-de-la-UI → decisión del CONTRATO compartido.
 *
 * Es la red que faltaba: sin ella, una capacidad nueva se queda fuera de la
 * comprobación cruzada entre los dos runtimes, que es exactamente cómo se
 * colaron `ManageContent` (definida, testeada y sin gatear nada) y
 * `ManageIntegrations` (una sola capacidad para una lectura y una acción, con
 * `read_only` disparando correos). Toda capacidad o está aquí, o figura
 * eximida POR ESCRITO con su motivo en `CONTRACT_EXEMPT_CAPABILITIES`.
 */
export const CAPABILITY_TO_CONTRACT: Partial<Record<AdminCapability, string>> = {
  "users.read": "viewUsers",
  "users.ban": "manageUsers",
  "users.revokeSessions": "manageUsers",
  "users.setRole": "setRole",
  "users.remove": "removeUser",
  "audit.read": "viewAudit",
  "metrics.read": "viewMetrics",
  "integrations.read": "viewIntegrations",
  "integrations.testEmail": "manageIntegrations",
  "flags.read": "viewFlags",
  "flags.write": "manageFlags",
  "content.write": "manageContent",
  "moderation.read": "manageContent",
  "tickets.read": "manageContent",
};

/** Capacidades de la UI SIN decisión en el contrato, con su motivo escrito. */
export const CONTRACT_EXEMPT_CAPABILITIES: Record<string, string> = {
  "admin.access":
    "no es una decisión del contrato sino la puerta del back-office: en el servidor es `has_any_admin_capability` (¿no eres un usuario normal?), derivada del propio mapa.",
};
